<template>
    <div>
        <limitless-page-header>
            <template #header-elements>
                <limitless-page-header-button @click="$router.push({ name: 'birthday_lists.list' })" icon="icon-circle-left2">Tillbaka till lista</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div v-if="!initialized && busy_loading" class="content">
            <i class="icon-spinner2 spinner"></i>
        </div>
        <div v-else-if="!not_found && list" class="content" style="padding-bottom: 6rem;">
            <div class="card">
                <div class="card-body">
                    <form>
                        <ValidationObserver ref="listform">
                            <div class="row">
                                <div class="col-lg-3">
                                    <select-with-validation
                                        label="Kund"
                                        component="customer-filter"
                                        v-model="list.customer_id"
                                        :reduce="(opt) => opt.id"
                                        rules="required"
                                        :disabled="list.birthday_id > 0"
                                        :clearable="false"
                                    ></select-with-validation>
                                </div>
                                <div class="col-lg-3">
                                    <select-with-validation
                                        label="Start-/Pågående order"
                                        component="card-order-filter"
                                        v-model="list.card_order_id"
                                        :reduce="(opt) => opt.id"
                                        rules="required"
                                        :customer-id="list.customer_id"
                                        :clearable="false"
                                    ></select-with-validation>
                                </div>
                                <div class="col-lg-3">
                                    <select-with-validation
                                        label="Kortserie"
                                        component="card-serie-filter"
                                        v-model="list.card_series_id"
                                        :reduce="(opt) => opt.id"
                                        rules="required"
                                        :order-id="list.card_order_id"
                                        :clearable="false"
                                        only-series
                                    ></select-with-validation>
                                </div>
                                <div class="col-lg-3">
                                    <ValidationProvider vid="end_date" name="Slutdatum" rules="" v-slot="{ errors }">
                                        <label>Slutdatum</label>
                                        <v-date-picker v-model="list.end_date" mode="date" :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }" :masks="{ input: 'YYYY-MM-DD' }" show-iso-weeknumbers>
                                            <template v-slot="{ inputValue, togglePopover }">
                                                <div class="input-group mb-0">
                                                    <input class="form-control" :value="inputValue" @focus="togglePopover()">
                                                    <span class="input-group-append">
                                                        <button class="btn btn-light" type="button" @click="list.end_date = ''"><i class="icon-cross3"></i></button>
                                                    </span>
                                                </div>
                                            </template>
                                        </v-date-picker>
                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                    </ValidationProvider>
                                </div>
                                <div class="col-lg-3">
                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                        <input id="chk-active" type="checkbox" class="custom-control-input" v-model="list.active">
                                        <label for="chk-active" class="custom-control-label">Aktiv</label>
                                    </div>
                                </div>
                            </div>
                        </ValidationObserver>
                    </form>
                </div>
                <div class="card-footer text-right">
                    <limitless-button ref="btnsavelist" type="button" @click="saveList" icon="icon-floppy-disk" :busy="busy_saving">Spara</limitless-button>
                </div>
            </div>

            <div class="card" v-if="this.list.birthday_id > 0">
                <div class="card-body">
                    <csv-import
                        @csv:imported="$refs.recipientstable.reload()"
                        :start-header="allowedColumns.join(',')"
                        :allowed-columns="allowedColumns"
                        :api-endpoint-url="'/vue/birthday_lists/' + this.list.birthday_id + '/import'"
                    ></csv-import>
                </div>
            </div>

            <rednax-datatable
                v-if="this.list.birthday_id > 0"
                ref="recipientstable"
                :url="'/vue/birthday_lists/' + list.birthday_id + '/recipients'"
                :columns="table_columns"
                :filter="table_filter"
                sort-by="days_to_next_birthday"
                sort-order="asc"
                :per-page="100"
                @rowclick="editRecipient"
            >
                <div class="row">
                    <div class="col-lg-3">
                        <input type="text" v-model="table_filter.search" class="form-control" placeholder="Sök...">
                    </div>
                </div>
                <template #header>
                    <h5 class="card-title">Mottagare</h5>
                    <div class="header-elements">
                        <button type="button" class="btn btn-light ml-2" @click="newRecipient"><i class="icon-plus3 mr-1"></i>Lägg till mottagare</button>
                    </div>
                </template>
            </rednax-datatable>

            <b-modal id="modal-recipient-edit" size="lg" @ok="saveRecipient" title="Redigera mottagare">
                <ValidationObserver ref="recipientform" v-if="recipient">
                    <form @submit="saveRecipient">
                        <input-with-validation
                            vid="name"
                            label="Namn"
                            v-model="recipient.name"
                            rules="required"
                        ></input-with-validation>
                        <input-with-validation
                            vid="email"
                            label="E-post"
                            v-model="recipient.email"
                            rules="required"
                        ></input-with-validation>
                        <ValidationProvider vid="birthdate" name="Födelsedatum" rules="required" v-slot="{ errors }">
                            <div class="form-group">
                                <label>Födelsedatum<span class="text-danger">*</span></label>
                                <v-date-picker v-model="recipient.birthdate" mode="date" :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }" :masks="{ input: 'YYYY-MM-DD' }">
                                    <template v-slot="{ inputValue, togglePopover }">
                                        <input class="form-control" :value="inputValue" @focus="togglePopover()" :class="{'has-validation-error':(!!errors[0])}">
                                    </template>
                                </v-date-picker>
                                <span class="validation-invalid-label">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>
                    </form>
                </ValidationObserver>
                <template #modal-footer="{ cancel, ok }">
                    <limitless-button
                        ref="btndeleterecipient"
                        type="button"
                        btn-class="btn-danger"
                        icon="icon-trash"
                        @click="deleteRecipient"
                        :busy="busy_delete_recipient"
                        class="mr-auto"
                    >
                        Radera
                    </limitless-button>
                    <button
                        type="button"
                        class="btn btn-light"
                        @click="cancel"
                    >
                        Avbryt
                    </button>
                    <limitless-button
                        ref="btnrecipientform"
                        type="button"
                        @click.prevent="ok"
                        :busy="busy_recipient"
                    >
                        Spara
                    </limitless-button>
                </template>
            </b-modal>
        </div> <!-- /content -->
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent';
import apiClient from '@services/ApiClient';
import LimitlessButton from '@components/Limitless/LimitlessButton.vue';
import getError from '@services/ErrorHelper';
import CsvImport from '@components/DataTools/CsvImport.vue';
import SelectWithValidation from '@components/Form/SelectWithValidation.vue';
import InputWithValidation from '@components/Form/InputWithValidation.vue';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';

export default {
    name: 'BirthdayListEdit',
    components: {
        VDatePicker,
        InputWithValidation,
        SelectWithValidation,
        CsvImport,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
        RednaxDatatable,
    },
    props: {
        id: {
            type: [String, Number],
            required: true,
        },
    },
    data: () => ({
        not_found: false,
        initialized: false,
        busy_loading: true,
        busy_saving: false,
        busy_deleting: false,
        recipient: null,
        busy_recipient: false,
        busy_delete_recipient: false,
        list: null,
        table_filter: {
            search: '',
        },
        table_columns: [
            {
                data: 'name',
                header: 'Name',
                sortable: true,
            },
            {
                data: 'email',
                header: 'E-postadress',
                sortable: true,
            },
            {
                data: 'birthdate',
                header: 'Födelsedatum',
                sortable: true,
            },
            {
                data: 'next_birthday',
                header: 'Nästa födelsedag',
                sortable: true,
            },
            {
                data: 'days_to_next_birthday',
                header: 'Dagar till nästa födelsedag',
                sortable: true,
            },
        ],
        allowedColumns: [
            'name',
            'email',
            'birthdate',
        ],
    }),
    mounted() {
        this.init();
    },
    watch: {
        id() {
            this.init();
        },
    },
    methods: {
        init() {
            this.getList();
        },
        getList() {
            this.busy_loading = true;
            apiClient.get('/vue/birthday_lists/' + this.id)
                .then((response) => {
                    this.list = response.data;
                })
                .catch((error) => {
                    const { status } = error.response;
                    if (status === 404) {
                        this.not_found = true;
                    }
                })
                .finally(() => {
                    this.initialized = true;
                    this.busy_loading = false;
                });
        },
        async saveList() {
            const isValid = await this.$refs.listform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/birthday_lists/' + this.list.birthday_id, { ...this.list })
                    .then((response) => {
                        if (parseInt(this.id, 10) !== parseInt(response.data.birthday_id, 10)) {
                            this.$router.push({ name: 'birthday_lists.edit', params: { id: response.data.birthday_id } });
                        }
                        this.list = response.data;
                        this.$refs.btnsavelist.triggerSaved();
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.listform.setErrors(parsederror);
                        }
                        this.$refs.btnsavelist.triggerFailed();
                    }).finally(() => {
                        this.busy_saving = false;
                    });
            } else {
                this.$refs.btnsavelist.triggerFailed();
            }
        },
        newRecipient() {
            this.recipient = {
                birthday_recipient_id: 0,
                birthday_id: this.id,
                name: '',
                birthdate: '',
                email: '',
            };
            this.$bvModal.show('modal-recipient-edit');
        },
        editRecipient(recipient) {
            this.recipient = { ...recipient };
            this.$bvModal.show('modal-recipient-edit');
        },
        async saveRecipient(event) {
            event.preventDefault();
            if (this.recipient) {
                const isValid = await this.$refs.recipientform.validate();
                if (isValid) {
                    this.busy_recipient = true;
                    apiClient.post(`/vue/birthday_lists/recipients/${this.recipient.birthday_recipient_id}`, this.recipient).then(() => {
                        this.$refs.btnrecipientform.triggerSaved();
                        this.$bvModal.hide('modal-recipient-edit');
                        this.$refs.recipientstable.reload();
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.recipientform.setErrors(parsederror);
                        }
                        this.$refs.btnrecipientform.triggerFailed();
                    }).finally(() => {
                        this.busy_recipient = false;
                    });
                } else {
                    this.$refs.btnrecipientform.triggerFailed();
                }
            }
        },
        async deleteRecipient() {
            let doDelete = false;
            if (this.recipient) {
                await this.$swal({
                    text: 'Är du säker att du vill radera denna mottagare?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Ja, radera!',
                }).then((result) => {
                    if (result.isConfirmed) {
                        doDelete = true;
                    }
                });
            }
            if (doDelete) {
                this.busy_delete_recipient = true;
                await apiClient.delete(`/vue/birthday_lists/recipients/${this.recipient.birthday_recipient_id}`)
                    .then(() => {
                        this.$bvModal.hide('modal-recipient-edit');
                        this.$refs.recipientstable.reload();
                    }).catch(() => {
                        this.$refs.btndeleterecipient.triggerFailed();
                    }).finally(() => {
                        this.busy_delete_recipient = false;
                    });
            }
        },
    },
};

</script>
